import {
  isUnoptimizedServer,
  type McpServer,
  type McpTool,
  type ToolGroup,
} from "./types";

const WHITESPACE = /\s+/g;

/**
 * Lowercase and strip *all* whitespace. Used both per-field (so a server named
 * "Google Calendar" is searchable as "googlecalendar") and to build a
 * whitespace-free key for the whole query (for exact/prefix ranking).
 */
export const normalizeSearchQuery = (text: string): string =>
  text.toLowerCase().replace(WHITESPACE, "");

/**
 * Split a raw query into lowercased, whitespace-free tokens, dropping blanks.
 * Multi-word queries become multiple tokens that are matched independently and
 * order-insensitively (see {@link matchesAllTokens}), so "create issue" and
 * "issue create" both surface a "Create issue" tool.
 */
export const tokenizeQuery = (query: string): string[] =>
  query.toLowerCase().split(WHITESPACE).filter(Boolean);

// Fields are joined with a space so a single space-free token can never match
// across a field boundary (e.g. server "Slack" + tool "Search" must not be
// matched by the token "slacksearch").
const FIELD_SEP = " ";

const buildHaystack = (fields: Array<string | undefined>): string =>
  fields
    .map((field) => (field ? normalizeSearchQuery(field) : ""))
    .join(FIELD_SEP);

/** AND semantics: every token must appear as a substring of the haystack. */
const matchesAllTokens = (haystack: string, tokens: string[]): boolean =>
  tokens.length > 0 && tokens.every((token) => haystack.includes(token));

// Searchable fields per entity. Tools are matched on their own name/description
// only — never the owning server — so the Tools tab stays distinct from the
// Servers tab (searching a server name belongs on the Servers tab).
const TOOL_FIELDS = (tool: McpTool): string[] => [
  tool.name,
  tool.description ?? "",
];
const SERVER_FIELDS = (server: McpServer): string[] => [
  server.displayName,
  server.name,
  server.description ?? "",
];

// "Primary" fields drive relevance ranking; a hit there outranks a
// description-only (secondary) hit.
const TOOL_PRIMARY = (tool: McpTool): string[] => [tool.name];
const SERVER_PRIMARY = (server: McpServer): string[] => [
  server.displayName,
  server.name,
];

// Match-field haystacks are pure functions of the (immutable) tool/server
// object, but the filter rebuilds them for every entity on every keystroke.
// Cache by object identity so each entity is normalized once across a session,
// not once per keystroke — the dominant cost when the full catalog is loaded.
const toolHaystackCache = new WeakMap<McpTool, string>();
const serverHaystackCache = new WeakMap<McpServer, string>();

const toolHaystack = (tool: McpTool): string => {
  const cached = toolHaystackCache.get(tool);
  if (cached !== undefined) {
    return cached;
  }
  const haystack = buildHaystack(TOOL_FIELDS(tool));
  toolHaystackCache.set(tool, haystack);
  return haystack;
};

const serverHaystack = (server: McpServer): string => {
  const cached = serverHaystackCache.get(server);
  if (cached !== undefined) {
    return cached;
  }
  const haystack = buildHaystack(SERVER_FIELDS(server));
  serverHaystackCache.set(server, haystack);
  return haystack;
};

const toolMatchesTokens = (tool: McpTool, tokens: string[]): boolean =>
  matchesAllTokens(toolHaystack(tool), tokens);

const serverMatchesTokens = (server: McpServer, tokens: string[]): boolean =>
  matchesAllTokens(serverHaystack(server), tokens);

/** Convenience wrappers that tokenize a raw query string before matching. */
export const toolMatches = (tool: McpTool, query: string): boolean =>
  toolMatchesTokens(tool, tokenizeQuery(query));

export const serverMatches = (server: McpServer, query: string): boolean =>
  serverMatchesTokens(server, tokenizeQuery(query));

/**
 * A drilled-in server's tools narrowed by the tools-view search box. A blank
 * query returns the server's whole tool list (the default drill-in view);
 * a non-blank one keeps only tools whose own name/description match.
 */
export const filterServerTools = (
  server: McpServer,
  query: string
): McpTool[] => {
  const tokens = tokenizeQuery(query);
  return tokens.length === 0
    ? server.tools
    : server.tools.filter((tool) => toolMatchesTokens(tool, tokens));
};

/**
 * Relevance of an already-matched entity, used only to order results:
 *   3 — a primary field equals the whole query (exact)
 *   2 — a primary field starts with the whole query (prefix)
 *   1 — every token is somewhere in a primary (name) field
 *   0 — matched via a secondary (description) field only
 */
const relevanceScore = (
  primaryFields: string[],
  tokens: string[],
  wholeQuery: string
): number => {
  const primaryHaystack = buildHaystack(primaryFields);
  if (!matchesAllTokens(primaryHaystack, tokens)) {
    return 0;
  }
  const normalized = primaryFields.map(normalizeSearchQuery);
  if (normalized.some((field) => field === wholeQuery)) {
    return 3;
  }
  if (normalized.some((field) => field.startsWith(wholeQuery))) {
    return 2;
  }
  return 1;
};

// Array.prototype.sort is stable, so equal-score items keep their catalog order.
const byScoreDesc = <T>(scored: Array<{ item: T; score: number }>): T[] =>
  scored.sort((a, b) => b.score - a.score).map((entry) => entry.item);

export type UnoptimizedMatch = { serverCount: number; toolCount: number };

export type McpPickerSearchResult = {
  filteredServers: McpServer[];
  filteredToolGroups: ToolGroup[];
  filteredToolCount: number;
  unoptimizedMatch: UnoptimizedMatch;
};

export function buildSearchResult({
  query,
  servers,
  showUnoptimizedMatches,
}: {
  query: string;
  servers: McpServer[];
  showUnoptimizedMatches: boolean;
}): McpPickerSearchResult {
  const tokens = tokenizeQuery(query);
  const hasSearch = tokens.length > 0;
  const wholeQuery = normalizeSearchQuery(query);

  // Hidden "unoptimized" servers are excluded from the working set unless the
  // user opted to reveal them.
  const visibleServers = showUnoptimizedMatches
    ? servers
    : servers.filter((server) => !isUnoptimizedServer(server));

  if (!hasSearch) {
    return {
      filteredServers: visibleServers,
      filteredToolGroups: [],
      filteredToolCount: 0,
      unoptimizedMatch: { serverCount: 0, toolCount: 0 },
    };
  }

  const filteredServers = byScoreDesc(
    visibleServers
      .filter((server) => serverMatchesTokens(server, tokens))
      .map((server) => ({
        item: server,
        score: relevanceScore(SERVER_PRIMARY(server), tokens, wholeQuery),
      }))
  );

  const filteredToolGroups = byScoreDesc(
    visibleServers
      .map((server) => {
        const tools = byScoreDesc(
          server.tools
            .filter((tool) => toolMatchesTokens(tool, tokens))
            .map((tool) => ({
              item: tool,
              score: relevanceScore(TOOL_PRIMARY(tool), tokens, wholeQuery),
            }))
        );
        return { server, tools };
      })
      .filter((group) => group.tools.length > 0)
      .map((group) => ({
        item: group,
        // Tools are pre-ranked, so the first tool carries the group's best score.
        score: relevanceScore(TOOL_PRIMARY(group.tools[0]), tokens, wholeQuery),
      }))
  );

  const filteredToolCount = filteredToolGroups.reduce(
    (total, group) => total + group.tools.length,
    0
  );

  const unoptimizedMatch = showUnoptimizedMatches
    ? { serverCount: 0, toolCount: 0 }
    : countUnoptimizedMatches(servers, tokens);

  return {
    filteredServers,
    filteredToolGroups,
    filteredToolCount,
    unoptimizedMatch,
  };
}

function countUnoptimizedMatches(
  servers: McpServer[],
  tokens: string[]
): UnoptimizedMatch {
  const hidden = servers.filter((server) => isUnoptimizedServer(server));
  return {
    serverCount: hidden.filter((server) => serverMatchesTokens(server, tokens))
      .length,
    toolCount: hidden.reduce(
      (total, server) =>
        total +
        server.tools.filter((tool) => toolMatchesTokens(tool, tokens)).length,
      0
    ),
  };
}
